import { useEffect, useState } from 'react';
import { RUNNING_VERSION, fetchDeployedVersion, isStale } from './appVersion';

/** Poll interval. Long enough to be free, short enough that a fix reaches someone the same session. */
const EVERY_MS = 5 * 60 * 1000;

/**
 * Watches for a newer deployed build.
 *
 * Checks on mount, on a timer, and whenever the tab regains focus — the last is what catches the common case:
 * a workstation left open all day, where the deploy happened while nobody was looking at it. It never checks
 * a hidden tab, so a background window is not polling for nothing.
 */
export function useAppVersion(): { stale: boolean; running: string; deployed: string | null } {
  const [deployed, setDeployed] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (document.visibilityState === 'hidden') return;
      const v = await fetchDeployedVersion();
      if (!cancelled && v) setDeployed(v);
    };
    void check();
    const timer = setInterval(() => void check(), EVERY_MS);
    const onFocus = () => void check();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, []);

  return { stale: isStale(RUNNING_VERSION, deployed), running: RUNNING_VERSION, deployed };
}
